import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Flex, FormField, Loader, Select, TextInput, SelectOption } from '@percent/lemonade';
import { Card, CountrySelect, FileUpload } from '@percent/hosted-validation-form/ui';
import { useHostedValidationsFormAnalytics } from '@percent/hosted-validation-form/hooks/useHostedValidationsFormAnalytics/useHostedValidationsFormAnalytics';
import { ErrorCard } from '@percent/hosted-validation-form/ui/ErrorCard/ErrorCard';
import { ErrorVariant } from '@percent/domain/verifications';
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData';

import { HostedFormStep } from '../../HostedForm.types';
import { RegisterOrganisationProps } from './RegisterOrganisation.types';
import { useProgress } from '@percent/hosted-validation-form/hooks';

type FormFieldValues = {
  registryName: string;
  registryId: string;
  organisationName: string;
  unknownRegistryName: string;
  file: File;
};

export const RegisterOrganisation = ({
  isLoading,
  isError,
  registries,
  isInitiallyFetching,
  onUpload,
  isUploading,
}: RegisterOrganisationProps) => {
  const { setCountry, country, handleNext, handleBack } = useValidationStepData();
  const {
    formState: { errors },
    getValues,
    setValue,
    watch,
    resetField,
    register,
    control,
  } = useFormContext<FormFieldValues>();
  const { t } = useTranslation();
  const { track } = useHostedValidationsFormAnalytics();
  const { progress } = useProgress();

  const [registryName] = useWatch({ control, name: ['registryName'] });

  const CANT_FIND_REGISTRY = t('hostedValidation.typography.cantFindRegistryName');

  const handleCountryChange = (newValue: SelectOption | null) => {
    setCountry(newValue?.value || '');
    setValue('registryName', '');
  };

  const handleNavigateNext = () => {
    if (getValues('registryName') === CANT_FIND_REGISTRY) {
      setValue('registryName', getValues('unknownRegistryName'));
    }

    return handleNext(HostedFormStep.ORGANISATION_ADDRESS);
  };

  const resetUnknownRegistryName = () =>
    getValues('registryName') !== CANT_FIND_REGISTRY && resetField('unknownRegistryName');

  const handleNavigateBack = () => {
    setValue('registryName', '');
    setValue('registryId', '');
    setValue('organisationName', '');
    handleBack();
  };

  useEffect(() => {
    if (isError) {
      track('Viewed RegisterOrganisation Error', {
        errorLabel: ErrorVariant.INTERNAL,
      });
    }
  }, [isError]);

  const registryNameOptions = useMemo(
    () => registries?.map((registry) => ({ value: registry.name, label: registry.name })) ?? [],
    [registries],
  );

  const registryNameValue = useMemo(() => {
    if (registryName === CANT_FIND_REGISTRY) {
      return { value: CANT_FIND_REGISTRY, label: CANT_FIND_REGISTRY };
    }

    return registryNameOptions.find((option) => option.value === registryName) ?? { value: '', label: '' };
  }, [CANT_FIND_REGISTRY, registryName, registryNameOptions]);

  if (isError) {
    return (
      <ErrorCard
        title={t('hostedValidation.errorMessage.somethingWentWrong')}
        subtitle={t('hostedValidation.errorMessage.reloadPage')}
        variant={ErrorVariant.INTERNAL}
      />
    );
  }

  const isNextButtonDisabled =
    !country ||
    !watch('organisationName') ||
    (watch('registryName') === CANT_FIND_REGISTRY && !watch('unknownRegistryName')) ||
    !watch('registryName') ||
    !watch('registryId') ||
    !watch('file') ||
    Object.keys(errors).length > 0;

  const handleRegistryNameChange = (newValue: SelectOption | null) => {
    setValue('registryName', newValue?.value || '');
    resetUnknownRegistryName();
  };

  return (
    <Card
      onBack={handleNavigateBack}
      nextButtonDisabled={isNextButtonDisabled}
      onNext={handleNavigateNext}
      nextButtonLabel={t('hostedValidation.button.next')}
      title={t('hostedValidation.title.registerOrg')}
      progress={progress}
    >
      {isInitiallyFetching && <Loader />}
      {!isInitiallyFetching && !isError && (
        <Flex direction="column" gap={16} w="100%">
          <FormField
            label={t('hostedValidation.label.organizationName')}
            status={errors.organisationName?.message ? 'danger' : 'default'}
            necessity="required"
            statusMessage={errors.organisationName?.message}
          >
            <TextInput {...register('organisationName')} placeholder={t('hostedValidation.placeholder.org')} />
          </FormField>
          <FormField label={t('hostedValidation.label.country')} necessity="required">
            <CountrySelect
              placeholder={t('hostedValidation.placeholder.country')}
              value={country}
              onChange={handleCountryChange}
            />
          </FormField>
          <FormField label={t('hostedValidation.label.registryName')} necessity="required">
            <Select
              placeholder={t('hostedValidation.placeholder.registryName')}
              defaultValue={registryNameValue}
              onChange={handleRegistryNameChange}
              disabled={isLoading}
              searchable
              options={registryNameOptions}
              addOption={{
                label: CANT_FIND_REGISTRY,
                onClick: () => {
                  setValue('registryName', CANT_FIND_REGISTRY);
                  track('Element Clicked', {
                    action: 'menu item',
                    label: "I can't find my registry name. I want to add it.",
                  });
                },
              }}
            />
          </FormField>
          {watch('registryName') === CANT_FIND_REGISTRY && (
            <FormField
              label={t('hostedValidation.label.registryName')}
              status={errors.unknownRegistryName?.message ? 'danger' : 'default'}
              necessity="required"
              statusMessage={errors.unknownRegistryName?.message}
            >
              <TextInput
                {...register('unknownRegistryName')}
                placeholder={t('hostedValidation.placeholder.registryName')}
              />
            </FormField>
          )}
          <FormField
            label={t('hostedValidation.label.registryId')}
            status={errors.registryId?.message ? 'danger' : 'default'}
            necessity="required"
            statusMessage={errors.registryId?.message}
          >
            <TextInput {...register('registryId')} placeholder={t('hostedValidation.placeholder.registryId')} />
          </FormField>
          <FormField
            label={t('hostedValidation.label.evidenceFile')}
            necessity="required"
            status={errors.file?.message ? 'danger' : 'default'}
            description={t('hostedValidation.validation.fileValid', { minFileSize: '32KB', maxFileSize: '5MB' })}
            statusMessage={errors.file?.message}
          >
            <FileUpload
              isUploading={isUploading}
              onUpload={onUpload}
              name="evidence"
              helperText={t('hostedValidation.validation.fileValid', { minFileSize: '32KB', maxFileSize: '5MB' })}
            />
          </FormField>
        </Flex>
      )}
    </Card>
  );
};
